import { useMemo, useState } from 'react';

import { ApiCommand } from '~/lib/Api';
import { useApi } from '~/lib/hooks';
import { GlobalSearchSuggestion } from '~/lib/types/navbar/global-search';

const PAGE_SIZE = 15;

type ApiResponse<T> = {
  count?: number;
  data: T[];
  totalCount?: number; // inquiries API uses totalCount while others use count
};

export type SearchData = {
  handleSearch: (searchTerm: string, isFirstPage?: boolean) => void;
  isLoading: boolean;
  suggestions: GlobalSearchSuggestion[];
  totalCount: number;
};

type UseSearchDataProps<T> = {
  baselineTotalCount?: number;
  fetchUrl: string;
  filterKey: string;
  pageSize?: number;
  toAutoCompleteOptionMapper: (item: T) => GlobalSearchSuggestion;
};

export const useSearchData = <T,>({
  baselineTotalCount = 0,
  fetchUrl,
  filterKey,
  pageSize = PAGE_SIZE,
  toAutoCompleteOptionMapper,
}: UseSearchDataProps<T>): SearchData => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [dataList, setDataList] = useState<T[]>([]);

  const { loading: isLoading, sendRequest } = useApi<ApiResponse<T>>();

  const updateDataList =
    (newPageNumber: number) =>
    (results: ApiResponse<T>): void => {
      if (!results) return;
      const { count, data: dataArray = [], totalCount } = results;
      setDataList((curr: T[]): T[] =>
        newPageNumber === 1 ? dataArray : [...curr, ...dataArray],
      );
      const correctCount = count ?? totalCount ?? 0;
      setTotalCount(correctCount + baselineTotalCount);
      setPageNumber(newPageNumber);
    };

  const handleSearch = (searchTerm: string, isFirstPage = true): void => {
    const newPageNumber = isFirstPage ? 1 : pageNumber + 1;
    const options = {
      'page-number': newPageNumber,
      'page-size': pageSize,
      'sort-by': 'name',
    };
    if (searchTerm) {
      options['filter-by'] = {
        [filterKey]: searchTerm
          .trim()
          .split(' ')
          .map((s) => encodeURIComponent(s))
          .join('+'),
      };
    }
    sendRequest({
      command: ApiCommand.GET,
      url: fetchUrl,
      options,
      callback: updateDataList(newPageNumber),
    });
  };

  const suggestions = useMemo<GlobalSearchSuggestion[]>(
    () => (dataList ?? []).map(toAutoCompleteOptionMapper),
    [dataList],
  );

  return {
    handleSearch,
    isLoading,
    suggestions,
    totalCount,
  };
};
